import { useLanguage } from "../contexts/LanguageContext";
import { useEffect } from "react";
import {
  HIDDEN_FIELD_TYPE,
  isHiddenFieldType,
  toParameterName,
} from "../constants/fieldTypes";

export default function CustomFieldCreator({
  field,
  onFieldChange,
  onRemoveField,
  removeable = true,
  isFieldRequired = false,
}) {
  const { t } = useLanguage();

  const isHidden = isHiddenFieldType(field.type);

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    const becomesHidden = isHiddenFieldType(newType);

    onFieldChange({
      ...field,
      type: newType,
      options: field.options || [],
      // The key follows the label for a visible field and is the query
      // parameter name for a hidden one, so it is rewritten only when the
      // field crosses between the two. Becoming hidden seeds a parameter name
      // from the label as a starting point; going back restores the mirroring.
      ...(becomesHidden !== isHidden && {
        field: becomesHidden ? toParameterName(field.label) : field.label,
      }),
      // Nobody fills a hidden field in, so requiring it would only turn away
      // the visitors who arrive without the parameter — most of them.
      ...(becomesHidden && { required: false }),
    });
  };

  const handleLabelChange = (e) => {
    onFieldChange({
      ...field,
      label: e.target.value,
      // A hidden field's key is its query parameter name, edited on its own
      // below: the parameter the marketing site appends has no reason to match
      // the label staff read in the dashboard.
      ...(isHidden ? {} : { field: e.target.value }),
    });
  };

  const handleParameterChange = (e) => {
    onFieldChange({
      ...field,
      // Surrounding whitespace is never part of a parameter name and is easy
      // to paste in by accident; it would silently stop the field matching.
      field: e.target.value.trim(),
    });
  };

  const handleOptionChange = (optionIndex, value) => {
    const newOptions = [...field.options];
    newOptions[optionIndex] = value;
    onFieldChange({
      ...field,
      options: newOptions,
    });
  };

  const handleRemoveOption = (optionIndex) => {
    const newOptions = field.options.filter((_, i) => i !== optionIndex);
    onFieldChange({
      ...field,
      options: newOptions,
    });
  };

  const handleAddOption = () => {
    const currentOptions = field.options || [];
    onFieldChange({
      ...field,
      options: [...currentOptions, ""],
    });
  };

  const handleRequiredChange = (e) => {
    onFieldChange({
      ...field,
      required: e.target.checked,
    });
  };

  useEffect(() => {
    if (isFieldRequired) {
      onFieldChange({
        ...field,
        required: true,
      });
    }
  }, [isFieldRequired]);

  return (
    <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-xs hover:shadow-md transition-shadow mb-4">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("forms.new.fields.fieldType")}
          </label>
          <select
            value={field.type}
            onChange={handleTypeChange}
            className="w-full rounded-lg border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="text">{t("forms.new.fields.types.text")}</option>
            <option value="date">{t("forms.new.fields.types.date")}</option>
            <option value="select">{t("forms.new.fields.types.select")}</option>
            <option value="radio">{t("forms.new.fields.types.radio")}</option>
            <option value="checkbox">
              {t("forms.new.fields.types.checkbox")}
            </option>
            <option value={HIDDEN_FIELD_TYPE}>
              {t("forms.new.fields.types.hidden")}
            </option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("forms.new.fields.label.required")}
          </label>
          <input
            required
            type="text"
            value={field.label}
            onChange={handleLabelChange}
            className="w-full rounded-lg border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
            placeholder={t("forms.new.fields.label.placeholder")}
          />
        </div>
      </div>

      {/* Query parameter the value is read from, for hidden fields */}
      {isHidden && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("forms.new.fields.urlParameter.label")}
          </label>
          <input
            required
            type="text"
            value={field.field || ""}
            onChange={handleParameterChange}
            className="w-full rounded-lg border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
            placeholder={t("forms.new.fields.urlParameter.placeholder")}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t("forms.new.fields.urlParameter.description")}
          </p>
        </div>
      )}

      {/* Options section for select, radio, and checkbox types */}
      {["select", "radio", "checkbox"].includes(field.type) && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("forms.new.fields.options.label")}
          </label>
          <div className="space-y-2">
            {field.options?.map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) =>
                    handleOptionChange(optionIndex, e.target.value)
                  }
                  className="flex-1 rounded-lg border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder={t("forms.new.fields.options.optionPlaceholder")}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveOption(optionIndex)}
                  className="text-red-600 hover:text-red-800"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddOption}
              className="inline-flex items-center px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors"
            >
              <svg
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t("forms.new.fields.options.addOption")}
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        {!isFieldRequired && !isHidden ? (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={field.required}
              onChange={handleRequiredChange}
              className="h-4 w-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label className="ml-2 text-sm text-gray-600">
              {t("forms.new.fields.required")}
            </label>
          </div>
        ) : (
          // Holds the left half of the row so justify-between keeps the remove
          // button on the right when there is no required toggle to show.
          <div />
        )}

        {removeable && (
          <button
            type="button"
            onClick={onRemoveField}
            className="inline-flex items-center px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
          >
            {t("common.remove")}
          </button>
        )}
      </div>
    </div>
  );
}
